import type { ReactNode } from 'react'

export type DescriptionListItem = {
  label: ReactNode
  value: ReactNode
}

export type DescriptionListColumns = 1 | 2 | 3

export type DescriptionListVariant = 'panel' | 'plain' | 'file-details'

export type DescriptionListPreviewState =
  | 'default'
  | 'plain'
  | 'one-column'
  | 'two-column'
  | 'with-badge'
  | 'claims-sample'
  | 'file-details'

export const DESCRIPTION_LIST_PREVIEW_STATES: DescriptionListPreviewState[] = [
  'default',
  'plain',
  'one-column',
  'two-column',
  'with-badge',
  'claims-sample',
  'file-details',
]

export const DESCRIPTION_LIST_PREVIEW_STATE_LABELS: Record<
  DescriptionListPreviewState,
  string
> = {
  default: 'Default (panel, 3-col)',
  plain: 'Plain',
  'one-column': 'One column',
  'two-column': 'Two column',
  'with-badge': 'With badge value',
  'claims-sample': 'Claims sample',
  'file-details': 'File details',
}

export const DESCRIPTION_LIST_DEMO_ITEMS: DescriptionListItem[] = [
  { label: 'Patient name', value: 'John Emerson' },
  { label: 'Payer', value: 'Delta Dental' },
  { label: 'Subscriber', value: 'John Emerson' },
  { label: 'Provider', value: 'Dr. Sarah Chen' },
  { label: 'Entity type', value: 'Individual' },
  { label: 'Claim ID', value: 'CLM-10482' },
  { label: 'Claim amount', value: '$1,250.00' },
  { label: 'Item type', value: 'Primary' },
  { label: 'Claim submitted', value: '03/12/2026' },
  { label: 'Check/EFT/Ref no.', value: 'EFT-99201' },
  { label: 'Paid amount', value: '$980.00' },
  { label: 'Mode of payment', value: 'EFT' },
  { label: 'Payment date', value: '03/18/2026' },
]

/** Explicit 3-column groups matching Claims Transaction info. */
export const DESCRIPTION_LIST_CLAIMS_GROUPS: DescriptionListItem[][] = [
  [
    { label: 'Patient name', value: 'John Emerson' },
    { label: 'Payer', value: 'Delta Dental' },
    { label: 'Subscriber', value: 'John Emerson' },
    { label: 'Provider', value: 'Dr. Sarah Chen' },
    { label: 'Entity type', value: 'Individual' },
  ],
  [
    { label: 'Claim ID', value: 'CLM-10482' },
    { label: 'Claim amount', value: '$1,250.00' },
    { label: 'Item type', value: 'Primary' },
    { label: 'Claim submitted', value: '03/12/2026' },
  ],
  [
    { label: 'Check/EFT/Ref no.', value: 'EFT-99201' },
    { label: 'Paid amount', value: '$980.00' },
    { label: 'Mode of payment', value: 'EFT' },
    { label: 'Payment date', value: '03/18/2026' },
  ],
]

/** Files → File Details modal rows (file-details variant). */
export const DESCRIPTION_LIST_FILE_DETAILS_ITEMS: DescriptionListItem[] = [
  { label: 'Name', value: 'Bitewing_radiograph_2026.pdf' },
  { label: 'File format', value: 'application/pdf' },
  { label: 'File size', value: '2.40 MB' },
  { label: 'Uploaded', value: 'Mar 12, 2026' },
  { label: 'Attached to claims:', value: 'Yes – View claim' },
  { label: 'Attached to forms:', value: 'No' },
  { label: 'Uploaded by:', value: 'Dr. Sarah Chen' },
]

export type DescriptionListProps = {
  /**
   * Flat list of rows. Distributed evenly across `columns`
   * when `groups` is not provided.
   */
  items?: DescriptionListItem[]
  /**
   * Explicit column groups (e.g. Claims Transaction info left / middle / right).
   * Takes precedence over `items` + `columns`.
   */
  groups?: DescriptionListItem[][]
  /** Column count when distributing `items`. Default `3`. */
  columns?: DescriptionListColumns
  /**
   * `panel` = tinted bordered surface (Claims details).
   * `plain` = no panel chrome.
   * `file-details` = dashed connectors between label and value (Files → File Details).
   */
  variant?: DescriptionListVariant
  className?: string
  darkMode?: boolean
  /** Static visual state for the design-system showcase only. */
  previewState?: DescriptionListPreviewState
}
